/**
 * Single field row card for the template detail page.
 * Shows grip handle, label, type icon, required badge, addon price.
 *
 * Plan: 117-personalization-admin-ui, Epic 01
 */

import { GripVertical, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { getFieldTypeIcon } from "./FieldTypeRadioCards";
import type { TemplateField } from "../api/customizationAdminTypes";

interface FieldCardProps {
  field: TemplateField;
  onEdit: () => void;
  onDelete: () => void;
  dragHandleProps?: Record<string, unknown>;
}

export function FieldCard({ field, onEdit, onDelete, dragHandleProps }: FieldCardProps) {
  const Icon = getFieldTypeIcon(field.field_type);

  return (
    <div
      className={cn(
        "group flex items-center gap-3 rounded-sm border bg-card p-3",
        "hover:border-border/80 hover:shadow-sm transition-all",
      )}
    >
      {/* Drag handle */}
      <div
        className="flex cursor-grab items-center text-muted-foreground/50 hover:text-muted-foreground active:cursor-grabbing"
        {...dragHandleProps}
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Main content — clickable for edit */}
      <button
        type="button"
        onClick={onEdit}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        {/* Type icon */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xs bg-muted">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Label + type */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{field.label}</p>
          <p className="text-xs text-muted-foreground">{field.field_type}</p>
        </div>

        {/* Badges */}
        <div className="flex shrink-0 items-center gap-2">
          {field.required === 1 && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              Required
            </span>
          )}
          {field.addon_price > 0 && (
            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
              +฿{field.addon_price.toLocaleString()}
            </span>
          )}
        </div>
      </button>

      {/* Delete button — visible on hover */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
